import { useState } from 'react';
import { 
  Paperclip, Send, Clock, User, AlertCircle, 
  CheckCircle, History, MoreVertical, Bold, Italic, 
  Underline, List, Link, Smile, Inbox, Mail, FileUp, Zap, Download,
  ChevronDown, X, Image as ImageIcon, Code, Undo, Redo, Eye, Save, Globe
} from 'lucide-react';
import type { Ticket } from '@/lib/mock';
import { cn } from '@/lib/utils';
import { useUsers } from '@/lib/useUsers';
import { useSettings } from '@/lib/useSettings';
import { useAuth } from '@/lib/auth';
import { useRef } from 'react';

function formatDateTime(ms: number) {
  return new Date(ms).toLocaleString([], {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

type Tab = 'conversation' | 'details' | 'attachments' | 'articles' | 'history';

export function UserTicketDetail({
  ticket,
  now,
  actions,
}: {
  ticket: Ticket;
  now: number;
  actions: {
    onStatus: (state: string) => void;
    onUpdate: (body: string, internal?: boolean) => void;
    onAddFile: (file: File) => void;
    onResolve: (state: string, code: string, notes: string) => void;
  };
}) {
  const [activeTab, setActiveTab] = useState<Tab>('conversation');
  const [replyMode, setReplyMode] = useState<'public' | 'internal' | 'email'>('public');
  const [replyText, setReplyText] = useState('');
  
  // Email Composer State
  const [emailTo, setEmailTo] = useState<string[]>([ticket.requester]);
  const [emailCc, setEmailCc] = useState<string[]>([]);
  const [emailBcc, setEmailBcc] = useState<string[]>([]);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [emailSubject, setEmailSubject] = useState(`Re: [${ticket.ref}] ${ticket.subject}`);
  const [emailFrom, setEmailFrom] = useState('support@company.com (IT Support)');

  const { profiles } = useUsers();
  const { mailboxes } = useSettings();
  const fileRef = useRef<HTMLInputElement>(null);
  const { user, session } = useAuth();
  const role = (user?.role || (session?.user as any)?.role || '').toLowerCase();
  const isStaffUser = role.includes('admin') || role.includes('technician') || role.includes('agent') || role.includes('manager') || role.includes('service desk');

  const assigneeProfile = profiles.find(p => p.id === (ticket.assigneeId || ticket.assignee));
  const assigneeName = ticket.assignee || assigneeProfile?.full_name || 'Unassigned';

  const handleReply = () => {
    if (!replyText.trim()) return;
    actions.onUpdate(replyText, isStaffUser && replyMode === 'internal');
    setReplyText('');
  };

  const slaRemaining = Math.max(0, ticket.slaDueAt - now);
  const slaRemainingHours = Math.floor(slaRemaining / 3600000);
  const slaRemainingMinutes = Math.floor((slaRemaining % 3600000) / 60000);

  return (
    <div className="flex-1 flex flex-col h-full bg-background overflow-hidden">
      {/* Header */}
      <div className="flex-none px-8 pt-8 pb-4 border-b border-border bg-card">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-500/10 text-blue-600">
              <Inbox className="h-7 w-7" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-display font-semibold tracking-tight text-foreground">{ticket.subject}</h1>
                <span className="rounded-md bg-muted px-2 py-1 text-xs font-mono font-medium text-muted-foreground">
                  {ticket.ref}
                </span>
              </div>
              <div className="mt-2 flex items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> Created on {formatDateTime(ticket.createdAt)}</span>
                <span className="flex items-center gap-1.5"><User className="h-4 w-4" /> Created by {ticket.requester}</span>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-muted-foreground">Status</span>
              <span className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium",
                ticket.state === 'Resolved' || ticket.state === 'Closed' ? "bg-emerald-500/10 text-emerald-600" :
                ticket.state === 'In Progress' ? "bg-blue-500/10 text-blue-600" :
                "bg-amber-500/10 text-amber-600"
              )}>
                <span className="h-2 w-2 rounded-full bg-current" />
                {ticket.state}
              </span>
            </div>
          </div>
        </div>

        {/* Tabs & Actions */}
        <div className="mt-8 flex items-center justify-between">
          <div className="flex items-center gap-6 border-b border-transparent">
            {[
              { id: 'conversation', label: 'Conversation' },
              { id: 'details', label: 'Details' },
              { id: 'attachments', label: `Attachments (${ticket.attachments?.length || 0})` },
              { id: 'articles', label: 'Related Articles' },
              { id: 'history', label: 'History' },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id as Tab)}
                className={cn(
                  "pb-3 text-sm font-medium transition-colors border-b-2",
                  activeTab === t.id ? "border-[var(--color-primary)] text-[var(--color-primary)]" : "border-transparent text-muted-foreground hover:text-foreground"
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 pb-2">
            <button className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted transition-colors">
              <Download className="h-4 w-4" /> Export
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto bg-muted/30 p-8">
        <div className="mx-auto max-w-7xl flex gap-8">
          
          {/* Left Column (Main) */}
          <div className="flex-1 flex flex-col gap-6">
            
            {(ticket.state === 'Resolved' || ticket.state === 'Closed') && (
              <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-6 shadow-sm animate-in fade-in slide-in-from-top-4">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 shrink-0 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600">
                    <CheckCircle className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-emerald-900 text-lg">Ticket Resolved</h3>
                    <p className="text-sm text-emerald-800/80 mt-1 mb-4">
                      This ticket was marked as resolved. If you're still experiencing issues, you can reopen it by replying or using the quick actions.
                    </p>
                    
                    {/* Resolution Notes */}
                    <div className="bg-background rounded-xl p-4 border border-border mb-4 shadow-sm">
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Resolution Summary</h4>
                      <p className="text-sm text-foreground leading-relaxed">
                        The issue has been resolved successfully. We have applied the necessary fixes and verified the system is functioning normally.
                      </p>
                    </div>

                    {/* CSAT Survey */}
                    <div className="bg-background rounded-xl p-5 border border-border shadow-sm">
                      <h4 className="text-sm font-semibold text-foreground mb-3">How satisfied are you with our support?</h4>
                      <div className="flex items-center gap-2 mb-4">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button key={star} className="text-muted-foreground hover:text-amber-500 transition-colors group">
                            <svg className="w-8 h-8 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                            </svg>
                          </button>
                        ))}
                      </div>
                      <textarea 
                        placeholder="Optional comments about your experience..." 
                        className="w-full text-sm rounded-lg border border-border bg-muted/30 p-3 outline-none focus:ring-1 focus:ring-primary/50 resize-none"
                        rows={2}
                      />
                      <div className="mt-3 flex justify-end">
                        <button className="bg-[var(--color-primary)] text-white px-5 py-2 rounded-lg text-sm font-medium hover:brightness-110 transition-all shadow-sm">
                          Submit Feedback
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'conversation' && (
              <>
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-lg font-semibold tracking-tight">Conversation</h2>
                </div>
                
                {/* Timeline */}
                <div className="flex flex-col gap-4">
                  {ticket.events.filter(e => (e.kind === 'comment' || e.kind === 'system') && (isStaffUser || !e.internal)).map((ev, i) => (
                    ev.kind === 'comment' ? (
                      <div key={i} className={cn(
                        "flex flex-col rounded-2xl border border-border p-5 shadow-sm transition-all",
                        ev.internal ? "bg-amber-500/5 border-amber-500/20" : "bg-card"
                      )}>
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--color-primary)] text-white font-bold text-sm uppercase">
                              {(ev.actor === '00000000-0000-0000-0000-000000000000' ? 'System' : profiles.find(p => p.id === ev.actor)?.full_name || profiles.find(p => p.id === ev.actor)?.email || ev.actor || 'U').charAt(0)}
                            </div>
                            <div>
                              <div className="font-semibold text-foreground flex items-center gap-2">
                                {ev.actor === '00000000-0000-0000-0000-000000000000' ? 'System' : profiles.find(p => p.id === ev.actor)?.full_name || profiles.find(p => p.id === ev.actor)?.email || ev.actor}
                                {ev.internal && <span className="rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] uppercase font-bold text-amber-700">Internal Note</span>}
                              </div>
                              <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
                                {(ev as any).source === 'email' ? (
                                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 px-2 py-0.5 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
                                    <Mail className="h-3 w-3" /> Email Reply
                                  </span>
                                ) : (ev as any).source === 'system' ? (
                                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-500/10 border border-slate-500/20 px-2 py-0.5 text-[11px] font-semibold text-slate-600 dark:text-slate-400">
                                    <Zap className="h-3 w-3" /> System
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 rounded-full bg-blue-500/10 border border-blue-500/20 px-2 py-0.5 text-[11px] font-semibold text-blue-600 dark:text-blue-400">
                                    <Globe className="h-3 w-3" /> Portal
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-muted-foreground font-mono">{formatDateTime(ev.at)}</span>
                            <button className="text-muted-foreground hover:text-foreground"><MoreVertical className="h-4 w-4" /></button>
                          </div>
                        </div>
                        <div className="text-sm leading-relaxed text-foreground whitespace-pre-wrap pl-[52px]">
                          {ev.label}
                        </div>
                      </div>
                    ) : (
                      <div key={i} className="flex items-center gap-3 py-1.5 px-4 text-xs text-muted-foreground">
                        <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />
                        <span>{ev.label}</span>
                        <span className="ml-auto font-mono text-[11px]">{formatDateTime(ev.at)}</span>
                      </div>
                    )
                  ))}
                </div>

                {/* Reply Composer */}
                <div className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden mt-2">
                  <div className="flex items-center justify-between border-b border-border bg-muted/20 px-4 py-2.5">
                    <div className="flex items-center gap-4">
                      <button 
                        onClick={() => setReplyMode('public')}
                        className={cn("text-sm font-medium transition-colors", replyMode === 'public' ? "text-[var(--color-primary)] font-semibold" : "text-muted-foreground")}
                      >
                        Public Reply
                      </button>
                      {isStaffUser && (
                        <>
                          <button 
                            onClick={() => setReplyMode('email')}
                            className={cn("text-sm font-medium flex items-center gap-1.5 transition-colors", replyMode === 'email' ? "text-[var(--color-primary)] border-b-2 border-[var(--color-primary)] -mb-[13px] pb-[11px]" : "text-muted-foreground")}
                          >
                            <Mail className="h-3.5 w-3.5" /> Email Reply
                          </button>
                          <button 
                            onClick={() => setReplyMode('internal')}
                            className={cn("text-sm font-medium flex items-center gap-1.5 transition-colors", replyMode === 'internal' ? "text-amber-600" : "text-muted-foreground")}
                          >
                            <AlertCircle className="h-3.5 w-3.5" /> Internal Note
                          </button>
                        </>
                      )}
                    </div>
                    {isStaffUser && replyMode === 'email' && (
                      <button className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                        <FileUp className="h-3.5 w-3.5" /> Email Template <ChevronDown className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  
                  {replyMode === 'email' && (
                    <div className="flex flex-col gap-3 p-4 bg-muted/10 border-b border-border text-sm">
                      <div className="flex items-center gap-4">
                        <span className="w-16 text-muted-foreground font-medium">From</span>
                        <div className="flex-1 flex items-center justify-between border border-border bg-background rounded-md px-3 py-1.5 focus-within:ring-1 focus-within:ring-primary/50 relative">
                          <select 
                            value={emailFrom}
                            onChange={(e) => setEmailFrom(e.target.value)}
                            className="w-full appearance-none bg-transparent outline-none text-sm z-10 relative cursor-pointer"
                          >
                            {mailboxes?.map(mbx => (
                              <option key={mbx.id} value={`${mbx.email} (${mbx.name})`}>{mbx.email} ({mbx.name})</option>
                            ))}
                          </select>
                          <ChevronDown className="h-4 w-4 text-muted-foreground absolute right-3 top-2 z-0 pointer-events-none" />
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-4">
                        <span className="w-16 text-muted-foreground font-medium mt-2">To</span>
                        <div className="flex-1 flex items-center justify-between">
                          <div className="flex-1 border border-border bg-background rounded-md px-2 py-1.5 flex flex-wrap gap-2 min-h-[36px]">
                            {emailTo.map((email, idx) => (
                              <span key={idx} className="flex items-center gap-1.5 bg-muted px-2 py-0.5 rounded-full text-xs">
                                {email} <button onClick={() => setEmailTo(emailTo.filter(e => e !== email))}><X className="h-3 w-3 text-muted-foreground hover:text-foreground" /></button>
                              </span>
                            ))}
                            <input type="text" placeholder="+ Add Email" className="bg-transparent outline-none text-sm min-w-[100px] flex-1" onKeyDown={(e) => {
                              if (e.key === 'Enter' && e.currentTarget.value) {
                                setEmailTo([...emailTo, e.currentTarget.value]);
                                e.currentTarget.value = '';
                              }
                            }}/>
                          </div>
                          <div className="flex items-center gap-2 ml-4 text-xs font-medium text-[var(--color-primary)]">
                            {!showCc && <button onClick={() => setShowCc(true)}>CC</button>}
                            {!showBcc && <button onClick={() => setShowBcc(true)}>BCC</button>}
                          </div>
                        </div>
                      </div>

                      {showCc && (
                        <div className="flex items-start gap-4">
                          <span className="w-16 text-muted-foreground font-medium mt-2">CC</span>
                          <div className="flex-1 border border-border bg-background rounded-md px-2 py-1.5 flex flex-wrap gap-2 min-h-[36px]">
                            {emailCc.map((email, idx) => (
                              <span key={idx} className="flex items-center gap-1.5 bg-muted px-2 py-0.5 rounded-full text-xs">
                                {email} <button onClick={() => setEmailCc(emailCc.filter(e => e !== email))}><X className="h-3 w-3 text-muted-foreground hover:text-foreground" /></button>
                              </span>
                            ))}
                            <input type="text" placeholder="+ Add Email" className="bg-transparent outline-none text-sm min-w-[100px] flex-1" onKeyDown={(e) => {
                              if (e.key === 'Enter' && e.currentTarget.value) {
                                setEmailCc([...emailCc, e.currentTarget.value]);
                                e.currentTarget.value = '';
                              }
                            }}/>
                          </div>
                        </div>
                      )}

                      {showBcc && (
                        <div className="flex items-start gap-4">
                          <span className="w-16 text-muted-foreground font-medium mt-2">BCC</span>
                          <div className="flex-1 border border-border bg-background rounded-md px-2 py-1.5 flex flex-wrap gap-2 min-h-[36px]">
                            {emailBcc.map((email, idx) => (
                              <span key={idx} className="flex items-center gap-1.5 bg-muted px-2 py-0.5 rounded-full text-xs">
                                {email} <button onClick={() => setEmailBcc(emailBcc.filter(e => e !== email))}><X className="h-3 w-3 text-muted-foreground hover:text-foreground" /></button>
                              </span>
                            ))}
                            <input type="text" placeholder="+ Add Email" className="bg-transparent outline-none text-sm min-w-[100px] flex-1" onKeyDown={(e) => {
                              if (e.key === 'Enter' && e.currentTarget.value) {
                                setEmailBcc([...emailBcc, e.currentTarget.value]);
                                e.currentTarget.value = '';
                              }
                            }}/>
                          </div>
                        </div>
                      )}

                      <div className="flex items-center gap-4">
                        <span className="w-16 text-muted-foreground font-medium">Subject</span>
                        <input 
                          type="text" 
                          value={emailSubject}
                          onChange={(e) => setEmailSubject(e.target.value)}
                          className="flex-1 border border-border bg-background rounded-md px-3 py-1.5 outline-none" 
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between px-4 py-2 border-b border-border/50 text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <button className="p-1.5 hover:bg-muted rounded"><Bold className="h-4 w-4" /></button>
                      <button className="p-1.5 hover:bg-muted rounded"><Italic className="h-4 w-4" /></button>
                      <button className="p-1.5 hover:bg-muted rounded"><Underline className="h-4 w-4" /></button>
                      <div className="w-px h-4 bg-border mx-1" />
                      <button className="p-1.5 hover:bg-muted rounded"><List className="h-4 w-4" /></button>
                      <button className="p-1.5 hover:bg-muted rounded"><List className="h-4 w-4 rotate-180" /></button>
                      <div className="w-px h-4 bg-border mx-1" />
                      <button className="p-1.5 hover:bg-muted rounded"><Link className="h-4 w-4" /></button>
                      <button className="p-1.5 hover:bg-muted rounded"><Smile className="h-4 w-4" /></button>
                      <button className="p-1.5 hover:bg-muted rounded"><Paperclip className="h-4 w-4" /></button>
                      <button className="p-1.5 hover:bg-muted rounded"><ImageIcon className="h-4 w-4" /></button>
                      <button className="p-1.5 hover:bg-muted rounded"><Code className="h-4 w-4" /></button>
                      <div className="w-px h-4 bg-border mx-1" />
                      <button className="p-1.5 hover:bg-muted rounded"><Undo className="h-4 w-4" /></button>
                      <button className="p-1.5 hover:bg-muted rounded"><Redo className="h-4 w-4" /></button>
                    </div>
                    {replyMode === 'email' && (
                      <button className="text-xs font-medium hover:text-foreground">Plain Text</button>
                    )}
                  </div>

                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder={replyMode === 'email' ? "Type your email here..." : "Type your reply here..."}
                    className={cn(
                      "w-full resize-none p-4 text-sm bg-transparent outline-none min-h-[120px]",
                      replyMode === 'email' ? "min-h-[200px]" : ""
                    )}
                  />

                  <div className="flex flex-wrap items-center justify-between gap-3 p-4 bg-muted/10 border-t border-border">
                    <button 
                      onClick={() => fileRef.current?.click()}
                      className="flex items-center gap-1.5 text-sm font-medium text-[var(--color-primary)] hover:underline whitespace-nowrap shrink-0"
                    >
                      <Paperclip className="h-4 w-4 shrink-0" /> Attach File
                    </button>
                    
                    {replyMode === 'email' ? (
                      <div className="flex flex-wrap items-center gap-2 lg:gap-3 justify-end flex-1">
                        <button className="flex items-center gap-1.5 text-sm font-medium border border-border bg-card px-3 py-2 rounded-lg hover:bg-muted transition-colors whitespace-nowrap shrink-0">
                          <Save className="h-4 w-4 text-muted-foreground shrink-0" /> Save Draft
                        </button>
                        <button className="flex items-center gap-1.5 text-sm font-medium border border-border bg-card px-3 py-2 rounded-lg hover:bg-muted transition-colors whitespace-nowrap shrink-0">
                          <Eye className="h-4 w-4 text-muted-foreground shrink-0" /> Preview Email
                        </button>
                        <div className="flex shrink-0">
                          <button 
                            onClick={handleReply}
                            disabled={!replyText.trim()}
                            className="inline-flex items-center gap-1.5 rounded-l-lg bg-[var(--color-primary)] px-4 lg:px-6 py-2.5 text-sm font-bold text-white shadow-md hover:brightness-110 disabled:opacity-50 disabled:pointer-events-none transition-all whitespace-nowrap"
                          >
                            <Send className="h-4 w-4 shrink-0" /> Send Email
                          </button>
                          <button className="inline-flex items-center justify-center rounded-r-lg bg-[var(--color-primary)] px-2 border-l border-white/20 text-white hover:brightness-110">
                            <ChevronDown className="h-4 w-4" />
                          </button>
                        </div>
                        <label className="flex items-center gap-1.5 text-sm text-muted-foreground ml-1 lg:ml-2 cursor-pointer whitespace-nowrap shrink-0">
                          <input type="checkbox" className="rounded border-border accent-[var(--color-primary)] shrink-0" />
                          Send me a copy
                        </label>
                      </div>
                    ) : (
                      <button 
                        onClick={handleReply}
                        disabled={!replyText.trim()}
                        className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] px-6 py-2.5 text-sm font-bold text-white shadow-md hover:brightness-110 disabled:opacity-50 disabled:pointer-events-none transition-all"
                      >
                        <Send className="h-4 w-4" /> Send Reply
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}

            {activeTab !== 'conversation' && (
              <div className="rounded-2xl border border-border bg-card p-8 shadow-sm flex flex-col items-center justify-center text-muted-foreground min-h-[400px]">
                <Clock className="h-12 w-12 mb-4 opacity-20" />
                <h3 className="text-lg font-medium text-foreground mb-1">Coming Soon</h3>
                <p>This tab is currently under construction in the new portal.</p>
              </div>
            )}
            
          </div>

          {/* Right Column (Sidebars) */}
          <div className="w-80 flex flex-col gap-6 shrink-0">
            
            {/* Ticket Information */}
            <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
              <h3 className="font-semibold tracking-tight text-foreground mb-4">Ticket Information</h3>
              <div className="space-y-4">
                <InfoRow label="Ticket ID" value={ticket.ref} />
                <InfoRow label="Status" value={<span className="text-blue-500 font-medium flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-blue-500"/>{ticket.state}</span>} />
                <InfoRow label="Priority" value={<span className={cn("font-medium", ticket.priority === 'P1' || ticket.priority === 'P2' ? "text-red-500" : "text-amber-500")}>↑ {ticket.priority === 'P1' ? 'Critical' : ticket.priority === 'P2' ? 'High' : ticket.priority === 'P3' ? 'Medium' : 'Low'}</span>} />
                <InfoRow label="Category" value={ticket.category} />
                <InfoRow label="Sub Category" value="N/A" />
                <InfoRow label="Assigned To" value={assigneeName} />
                <div className="pt-2 border-t border-border">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-foreground">SLA Due</span>
                    <span className="text-foreground font-mono text-xs">{formatDateTime(ticket.slaDueAt)}</span>
                  </div>
                  <div className="text-right text-xs font-medium text-emerald-500">
                    ({slaRemainingHours}h {slaRemainingMinutes}m remaining)
                  </div>
                </div>
              </div>
            </div>

            {/* Email Communication */}
            <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
              <h3 className="font-semibold tracking-tight text-foreground mb-4 flex items-center gap-2">
                <Mail className="h-4 w-4 text-blue-500" /> Email Communication
              </h3>
              <div className="space-y-4">
                <InfoRow label="From" value={ticket.requesterEmail ? `${ticket.requester} (${ticket.requesterEmail})` : ticket.requester} />
                <InfoRow label="To" value={mailboxes?.[0]?.email || 'techsupport@pinochle.ai'} />
                {Array.isArray(ticket.custom?.cc) && (ticket.custom.cc as string[]).length > 0 ? (
                  <InfoRow label="CC" value={(ticket.custom.cc as string[]).join(', ')} />
                ) : null}
                <InfoRow label="Channel" value={<span className="flex items-center gap-1.5 font-medium"><span className="text-blue-500 font-bold">M365</span> Microsoft Graph</span>} />
                <InfoRow label="Last Updated" value={formatDateTime(now)} />
              </div>
            </div>

            {/* Quick Actions */}
            <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
              <h3 className="font-semibold tracking-tight text-foreground mb-4">Quick Actions</h3>
              <div className="space-y-1">
                <input
                  ref={fileRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    Array.from(e.target.files ?? []).forEach((f) => actions.onAddFile(f));
                    e.target.value = '';
                  }}
                />
                <button 
                  onClick={() => fileRef.current?.click()}
                  className="flex w-full items-center gap-3 rounded-lg p-2 text-sm font-medium text-[var(--color-primary)] hover:bg-[var(--color-primary)]/10 transition-colors"
                >
                  <Paperclip className="h-4 w-4" /> Add Attachment
                </button>
                
                {(ticket.state === 'Resolved' || ticket.state === 'Closed') && (
                  <button 
                    onClick={() => actions.onStatus('Open')}
                    className="flex w-full items-center gap-3 rounded-lg p-2 text-sm font-medium text-foreground hover:bg-muted transition-colors"
                  >
                    <History className="h-4 w-4" /> Reopen Ticket
                  </button>
                )}

                <button 
                  onClick={() => actions.onUpdate('User has escalated this ticket.', false)}
                  className="flex w-full items-center gap-3 rounded-lg p-2 text-sm font-medium text-foreground hover:bg-muted transition-colors"
                >
                  <AlertCircle className="h-4 w-4" /> Escalate Ticket
                </button>

                {ticket.state !== 'Closed' && (
                  <button 
                    onClick={() => {
                      if (confirm('Are you sure you want to close this ticket?')) {
                        actions.onResolve('Closed', 'User Closed', 'Closed by requester via quick action');
                      }
                    }}
                    className="flex w-full items-center gap-3 rounded-lg p-2 text-sm font-medium text-foreground hover:bg-muted transition-colors"
                  >
                    <CheckCircle className="h-4 w-4" /> Close Ticket
                  </button>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between items-start text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground text-right max-w-[60%]">{value}</span>
    </div>
  );
}
